/* eslint-disable array-bracket-newline */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { BigNumber } from 'bignumber.js';
import { ITicker } from '@kattana-io/common';
import { Configuration } from '../helpers/elrondTrading';
import { ICoin } from '../reducers/dex/tradingReducer';
import { Direction } from '.';
import { estimateSuccessCallbackType } from './useTradeEstimate';
import { getPriceUSDForPair } from '../helpers/statistics';

const estimateQueryString = `
query ($amountIn: String, $amountOut: String, $tokenInID: String!, $tokenOutID: String!, $tolerance: Float!) {
    swap(
      amountIn: $amountIn
      amountOut: $amountOut
      tokenInID: $tokenInID
      tokenOutID: $tokenOutID
      tolerance: $tolerance
    ) {
      amountIn
      tokenInID
      tokenInPriceUSD
      tokenInExchangeRateDenom
      amountOut
      tokenOutID
      tokenOutPriceUSD
      tokenOutExchangeRateDenom
      fees
      swapType
      tokenRoute
      pricesImpact
      maxPriceDeviationPercent
      tokensPriceDeviationPercent
      intermediaryAmounts
      pairs {
        address
        firstToken {
          balance
          decimals
          name
          identifier
          ticker
          owner
          assets {
            website
            description
            status
            pngUrl
            svgUrl
            __typename
          }
          price
          type
          __typename
        }
        firstTokenPrice
        firstTokenPriceUSD
        firstTokenVolume24h
        firstTokenLockedValueUSD
        secondToken {
          balance
          decimals
          name
          identifier
          ticker
          owner
          assets {
            website
            description
            status
            pngUrl
            svgUrl
            __typename
          }
          price
          type
          __typename
        }
        secondTokenPrice
        secondTokenPriceUSD
        secondTokenVolume24h
        secondTokenLockedValueUSD
        liquidityPoolToken {
          balance
          decimals
          name
          identifier
          ticker
          owner
          assets {
            website
            description
            status
            pngUrl
            svgUrl
            __typename
          }
          price
          type
          __typename
        }
        state
        type
        lockedValueUSD
        info {
          reserves0
          reserves1
          totalSupply
          __typename
        }
        feesAPR
        feesUSD24h
        volumeUSD24h
        totalFeePercent
        specialFeePercent
        lockedTokensInfo {
          lockingSC {
            address
            lockedToken {
              assets {
                website
                description
                status
                pngUrl
                svgUrl
                __typename
              }
              decimals
              name
              collection
              ticker
              __typename
            }
            lpProxyToken {
              assets {
                website
                description
                status
                pngUrl
                svgUrl
                __typename
              }
              decimals
              name
              collection
              ticker
              __typename
            }
            farmProxyToken {
              assets {
                website
                description
                status
                pngUrl
                svgUrl
                __typename
              }
              decimals
              name
              collection
              ticker
              __typename
            }
            intermediatedPairs
            intermediatedFarms
            __typename
          }
          unlockEpoch
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export interface Assets {
  website: string;
  description: string;
  status: string;
  pngUrl: string;
  svgUrl: string;
  __typename: string;
}

export interface FirstToken {
  balance?: any;
  decimals: number;
  name: string;
  identifier: string;
  ticker: string;
  owner: string;
  assets: Assets;
  price: string;
  type: string;
  __typename: string;
}

export interface Assets2 {
  website: string;
  description: string;
  status: string;
  pngUrl: string;
  svgUrl: string;
  __typename: string;
}

export interface SecondToken {
  balance?: any;
  decimals: number;
  name: string;
  identifier: string;
  ticker: string;
  owner: string;
  assets: Assets2;
  price: string;
  type: string;
  __typename: string;
}

export interface Assets3 {
  website: string;
  description: string;
  status: string;
  pngUrl: string;
  svgUrl: string;
  __typename: string;
}

export interface LiquidityPoolToken {
  balance?: any;
  decimals: number;
  name: string;
  identifier: string;
  ticker: string;
  owner: string;
  assets: Assets3;
  price: string;
  type: string;
  __typename: string;
}

export interface Info {
  reserves0: string;
  reserves1: string;
  totalSupply: string;
  __typename: string;
}

export interface Pair {
  address: string;
  firstToken: FirstToken;
  firstTokenPrice: string;
  firstTokenPriceUSD: string;
  firstTokenVolume24h: string;
  firstTokenLockedValueUSD: string;
  secondToken: SecondToken;
  secondTokenPrice: string;
  secondTokenPriceUSD: string;
  secondTokenVolume24h: string;
  secondTokenLockedValueUSD: string;
  liquidityPoolToken: LiquidityPoolToken;
  state: string;
  type: string;
  lockedValueUSD: string;
  info: Info;
  feesAPR: string;
  feesUSD24h: string;
  volumeUSD24h: string;
  totalFeePercent: number;
  specialFeePercent: number;
  lockedTokensInfo?: any;
  __typename: string;
}

export interface Swap {
  amountIn: string;
  tokenInID: string;
  tokenInPriceUSD: string;
  tokenInExchangeRateDenom: string;
  amountOut: string;
  tokenOutID: string;
  tokenOutPriceUSD: string;
  tokenOutExchangeRateDenom: string;
  fees: string[];
  swapType: number;
  tokenRoute: string[];
  pricesImpact: string[];
  maxPriceDeviationPercent: number;
  tokensPriceDeviationPercent?: any;
  intermediaryAmounts: string[];
  pairs: Pair[];
  __typename: string;
}

export interface Data {
  swap: Swap;
}

export interface RootData {
  data: Data;
}

type FetchEstimateArgs =
  | {
      amountIn: string;
      tokenInID: string;
      tokenOutID: string;
      tolerance: number;
    }
  | {
      amountOut: string;
      tokenInID: string;
      tokenOutID: string;
      tolerance: number;
    };

const EGLD_TOKEN = 'EGLD';

const fetchEstimate = async (
  body: FetchEstimateArgs,
  controller: AbortController
) => {
  const variables: FetchEstimateArgs = {
    ...body,
    tokenInID: body.tokenInID || EGLD_TOKEN,
    tokenOutID: body.tokenOutID || EGLD_TOKEN,
  };
  const { data }: { data: RootData } = await axios.post(
    `${Configuration.exchangeGraph}`,
    {
      variables,
      query: estimateQueryString,
      signal: controller.signal,
    }
  );
  return data;
};

interface ElrondEstimateProps {
  meta?: ITicker;
  base: ICoin;
  quote: ICoin;
  direction: Direction; // 0 - fixed input, 1 - fixed output
  slippage: number; // 0.01 -> 1%
  estimateIndex: number;
  enabled: boolean;
  onEstimateStart: (base: boolean) => void;
  onEstimateError: () => void;
  onEstimateSuccess: (args: estimateSuccessCallbackType) => void;
}

type estimateType = (controller: AbortController) => Promise<void>;

const useElrondEstimate = ({
  meta,
  base,
  quote,
  direction,
  slippage,
  estimateIndex,
  enabled,
  onEstimateStart,
  onEstimateError,
  onEstimateSuccess,
}: ElrondEstimateProps) => {
  const [price, setPrice] = useState<BigNumber>(new BigNumber(0));
  const [priceUsd, setPriceUsd] = useState({
    base: BigNumber(0),
    quote: BigNumber(0),
  });
  const [tokenRoute, setTokenRoute] = useState<string[]>([]);
  const [priceImpact, setPriceImpact] = useState<number>(0);
  const [gasFees, setGasFees] = useState<number>(0);

  const estimateBaseQuote: estimateType = async (controller) => {
    onEstimateStart(false);

    try {
      const amountIn = new BigNumber(base.amount)
        .times(10 ** base.decimals)
        .toString();
      const tokenInID = base.address;
      const tokenOutID = quote.address;
      const body = {
        amountIn,
        tokenInID,
        tokenOutID,
        tolerance: slippage / 100,
      };

      const data = await fetchEstimate(body, controller);

      setPrice(new BigNumber(data.data.swap.tokenInExchangeRateDenom));
      setPriceUsd({
        base: new BigNumber(data.data.swap.tokenInPriceUSD),
        quote: new BigNumber(data.data.swap.tokenOutPriceUSD),
      });
      setTokenRoute(data.data.swap.tokenRoute);
      setPriceImpact(+data.data.swap.pricesImpact[0]);
      setGasFees(+data.data.swap.fees[0]);

      const naturalAmount = new BigNumber(data.data.swap.amountOut).div(
        10 ** Number(quote.decimals)
      );
      const afterSlippage = (100 - slippage) / 100;

      onEstimateSuccess({
        naturalAmount,
        afterSlippage,
      });
    } catch (error) {
      console.warn(error);
      if (!controller.signal.aborted) {
        onEstimateError();
      }
    }
  };

  /**
   * Effect 1
   * Base->Quote
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.BaseQuote && enabled) {
      if (base.amount.gt(0)) {
        estimateBaseQuote(controller);
      }
    }

    return () => {
      controller.abort();
    };
  }, [base, slippage, estimateIndex, enabled, direction]);

  useEffect(() => {
    setPriceUsd({
      base: BigNumber(getPriceUSDForPair(meta, base.address)),
      quote: BigNumber(getPriceUSDForPair(meta, quote.address)),
    });
  }, [meta]);

  const estimateQuoteBase: estimateType = async (controller) => {
    onEstimateStart(true);

    try {
      const amountOut = new BigNumber(quote.amount)
        .times(10 ** quote.decimals)
        .toString();
      const tokenInID = base.address;
      const tokenOutID = quote.address;
      const body = {
        amountOut,
        tokenInID,
        tokenOutID,
        tolerance: 0.01,
      };

      const data = await fetchEstimate(body, controller);

      setPrice(new BigNumber(data.data.swap.tokenInExchangeRateDenom));
      setPriceUsd({
        base: new BigNumber(data.data.swap.tokenInPriceUSD),
        quote: new BigNumber(data.data.swap.tokenOutPriceUSD),
      });
      setTokenRoute(data.data.swap.tokenRoute);
      setPriceImpact(+data.data.swap.pricesImpact[0]);
      setGasFees(+data.data.swap.fees[0]);

      const quoteNaturalAmount = new BigNumber(data.data.swap.amountOut).div(
        10 ** Number(quote.decimals)
      );
      const naturalAmount = new BigNumber(data.data.swap.amountIn).div(
        10 ** Number(base.decimals)
      );
      const afterSlippage = (100 - slippage) / 100;

      onEstimateSuccess({
        naturalAmount,
        quoteNaturalAmount,
        afterSlippage,
      });
    } catch (error) {
      console.warn(error);
      if (!controller.signal.aborted) {
        onEstimateError();
      }
    }
  };

  /**
   * Effect 2
   * Quote->Base
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.QuoteBase && enabled) {
      if (quote.amount.gt(0)) {
        estimateQuoteBase(controller);
      }
    }

    return () => {
      controller.abort();
    };
  }, [quote, slippage, estimateIndex, enabled, direction]);

  return { price, priceUsd, tokenRoute, gasFees, priceImpact };
};

export default useElrondEstimate;
